import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators, FormGroup, FormControl } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { EditorModule } from 'primeng/editor';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';

import { MessagesService } from '../../../services/messages.service';
import { MessageRequest, UserDTO } from '../../../model/admin-dto';
import { ValidatorComponent } from 'src/app/ui/validator/validator.component';
import { map, Observable } from 'rxjs';
import { UsersService } from 'src/app/services/users.service';

interface Recipient {
  name: string;
  code: string;
}

@Component({
  selector: 'rqa-create-message',
  templateUrl: './create-message.component.html',
  styles: [
    `
      ::ng-deep .multiselect-custom .p-multiselect-token-label {
        max-width: none;
      }

      ::ng-deep .multiselect-custom .p-multiselect-label {
        white-space: normal !important;
        height: auto;
        min-height: 2.5rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
        align-items: center;
        padding: 0.25rem 0.75rem;
      }

      ::ng-deep .multiselect-custom .p-multiselect-token {
        margin-right: 0.25rem;
        margin-bottom: 0.25rem;
      }
    `
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    EditorModule,
    TranslateModule,
    CheckboxModule,
    MultiSelectModule,
    ValidatorComponent
  ],
  providers: [DialogService, MessagesService]
})
export class CreateMessageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private messagesService = inject(MessagesService);
  private usersService = inject(UsersService);
  public ref = inject(DynamicDialogRef);
  public config = inject(DynamicDialogConfig);

  messageForm!: FormGroup;
  availableRecipients: Recipient[] = [];

  get isFormValid(): boolean {
    return this.messageForm.valid;
  }

  ngOnInit() {
    this.initializeForm();
    this.usersService.getUsersList().subscribe((users: UserDTO[]) => {
      this.availableRecipients = users.map((user: UserDTO) => ({
        name: user.username,
        code: user.username
      }));
    });
    
    if (this.config.data?.messageList) {
      this.messageForm.patchValue({
        recipient: this.config.data.messageList,
        sendToAll: this.config.data.sentToAll
      });
    }
  }

  onSendToAllChange(sendToAll?: boolean): void {
    const sendToAllValue = sendToAll ?? this.messageForm.get('sendToAll')?.value;
    if (sendToAllValue) {
      this.messageForm.patchValue({ recipient: [] });
    }
    this.messageForm.get('recipient')?.updateValueAndValidity();
  }

  send(): void {
    if (this.messageForm.valid) {
      const messageRequest: MessageRequest = this.messageForm.value;
      this.messagesService.sendMessage(messageRequest).subscribe((response) => {
        if (response) {
          this.ref.close(true);
        }
      });
    } else {
      this.messageForm.markAllAsTouched();
    }
  }

  cancel(): void {
    this.ref.close(false);
  }

  private initializeForm(): void {
    this.messageForm = this.fb.group({
      recipient: [[], [this.recipientValidator.bind(this)]],
      content: ['', [Validators.required]],
      title: ['', [Validators.required]],
      sendToAll: [false]
    });

    this.messageForm.get('sendToAll')?.valueChanges.subscribe((sendToAll: boolean) => {
      this.onSendToAllChange(sendToAll);
    });
  }

  private recipientValidator(control: FormControl) {
    const sendToAll = this.messageForm?.get('sendToAll')?.value;
    if (!sendToAll && (!control.value || control.value.length === 0)) {
      return { required: true };
    }
    return null;
  }

}
